import React, { useState, useCallback, useEffect, useMemo, useRef, memo } from 'react';
import { X, Search, Loader2, SlidersHorizontal } from 'lucide-react';
import { Input } from '@/components/ui/input';
import type { IconStyle } from '@builder4/shared/types/icon';
import { ICON_STYLE_SOLID, ICON_STYLE_REGULAR } from '@builder4/shared/utils/constants';
import { formatIconName } from '@builder4/shared/utils/iconHelpers';
import { getIconManager, type IconCategoriesDictionary } from '@builder4/shared/icons/manager';
import clsx from 'clsx';
import { buildCategoriesFromManager } from './iconCategories';

type IconData = {
  namesByStyle: Record<IconStyle, string[]>;
  categoriesByStyle: Record<IconStyle, IconCategoriesDictionary>;
};

interface IconPickerProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (name: string, style: IconStyle) => void;
  selectedIcon?: string;
  selectedStyle?: IconStyle;
}

interface IconButtonProps {
  iconName: string;
  formattedName: string;
  iconStyle: IconStyle;
  isSelected: boolean;
  onSelect: (iconName: string) => void;
}

// WHY: Memoized button prevents re-renders when other icons change state
const IconButton = memo<IconButtonProps>(function IconButton({
  iconName,
  formattedName,
  iconStyle,
  isSelected,
  onSelect,
}) {
  const handleClick = useCallback(() => {
    onSelect(iconName);
  }, [iconName, onSelect]);

  return (
    <button
      type="button"
      onClick={handleClick}
      className={clsx(
        'flex h-10 w-10 items-center justify-center rounded-lg border transition-colors',
        isSelected ? 'border-indigo-500 bg-indigo-50' : 'border-transparent hover:bg-neutral-100',
      )}
      title={formattedName}
    >
      <i className={`fa-${iconStyle} fa-${iconName} text-lg text-neutral-700`} />
    </button>
  );
});

export const IconPicker: React.FC<IconPickerProps> = ({
  isOpen,
  onClose,
  onSelect,
  selectedIcon,
  selectedStyle = ICON_STYLE_REGULAR,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [iconStyle, setIconStyle] = useState<IconStyle>(selectedStyle);
  const [showStyleFilter, setShowStyleFilter] = useState(false);
  const [iconData, setIconData] = useState<IconData | null>(null);
  const filterRef = useRef<HTMLDivElement>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const iconManager = getIconManager();

  // WHY: Build derived icon data (names, categories) once when picker opens
  useEffect(() => {
    if (!isOpen || iconData || !iconManager) return;

    const rawCategories = iconManager.getCategories();
    const styles: IconStyle[] = [ICON_STYLE_SOLID, ICON_STYLE_REGULAR];

    const namesByStyle = {} as Record<IconStyle, string[]>;
    const categoriesByStyle = {} as Record<IconStyle, IconCategoriesDictionary>;

    for (const style of styles) {
      const names = iconManager.getIconNamesByStyle(style);
      namesByStyle[style] = names;
      categoriesByStyle[style] = buildCategoriesFromManager(rawCategories, new Set(names));
    }

    setIconData({ namesByStyle, categoriesByStyle });
  }, [isOpen, iconData, iconManager]);

  // WHY: Debounce search to avoid filtering on every keystroke
  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 150);
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, [searchQuery]);

  // WHY: Close dropdown on outside click for expected UX behavior
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (filterRef.current && !filterRef.current.contains(event.target as Node)) {
        setShowStyleFilter(false);
      }
    };

    if (showStyleFilter) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showStyleFilter]);

  const styleNames = iconData?.namesByStyle[iconStyle] ?? [];

  // WHY: Search against name, label, and search terms for rich multi-term search
  const searchResults = useMemo(() => {
    if (!debouncedQuery.trim() || !iconManager || styleNames.length === 0) return null;

    const terms = debouncedQuery.toLowerCase().trim().split(/\s+/);

    return styleNames
      .filter((name) => {
        const label = iconManager.getIconLabel(name).toLowerCase();
        const searchTerms = iconManager.getSearchTerms(name).map((t) => t.toLowerCase());
        const searchable = [name, label, ...searchTerms].join(' ');

        return terms.every((term) => searchable.includes(term));
      })
      .slice(0, 100);
  }, [debouncedQuery, styleNames, iconManager]);

  // WHY: Filter categories only when not searching
  const filteredCategories = useMemo(() => {
    if (!iconData || debouncedQuery.trim()) return null;
    return iconData.categoriesByStyle[iconStyle];
  }, [iconData, debouncedQuery, iconStyle]);

  // WHY: Stable callback reference prevents IconButton re-renders
  const handleSelect = useCallback(
    (iconName: string) => {
      onSelect(iconName, iconStyle);
    },
    [onSelect, iconStyle],
  );

  if (!isOpen) return null;

  const isSearching = debouncedQuery.trim().length > 0;
  const hasResults = isSearching ? searchResults && searchResults.length > 0 : iconData !== null;

  return (
    <div className="absolute top-[252px] right-[calc(100%+8px)] z-50 flex flex-col">
      <div className="bg-neutral-0 flex h-[480px] w-[280px] flex-col rounded-lg border border-neutral-200 shadow-xl">
        <div className="flex items-center justify-between border-b border-neutral-100 px-4 py-3">
          <h2 className="text-sm font-semibold text-neutral-900">Select Icon</h2>
          <button
            type="button"
            onClick={onClose}
            className="flex h-6 w-6 items-center justify-center rounded-md text-neutral-400 hover:bg-neutral-100 hover:text-neutral-600"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="relative border-b border-neutral-100 px-4 py-3">
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Input
                placeholder="Search all icons..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="h-8 pr-8 text-xs"
              />
              <Search className="absolute top-1/2 right-2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
            </div>
            <div ref={filterRef} className="relative">
              <button
                type="button"
                onClick={() => setShowStyleFilter(!showStyleFilter)}
                className={clsx(
                  'flex h-8 w-8 items-center justify-center rounded-md border transition-colors',
                  showStyleFilter
                    ? 'border-indigo-500 bg-indigo-50 text-indigo-500'
                    : 'border-neutral-200 text-neutral-400 hover:border-neutral-300 hover:text-neutral-600',
                )}
              >
                <SlidersHorizontal className="h-4 w-4" />
              </button>

              {showStyleFilter && (
                <div className="absolute top-full right-0 z-10 mt-2 w-48 rounded-lg border border-neutral-200 bg-white p-3 shadow-lg">
                  <div className="mb-2 flex items-center gap-2">
                    <span className="text-xs font-semibold text-neutral-900">Style</span>
                  </div>

                  <div className="space-y-1">
                    <label className="flex cursor-pointer items-center justify-between rounded-md px-2 py-1.5 hover:bg-neutral-50">
                      <div className="flex items-center gap-2">
                        <input
                          type="radio"
                          name="iconStyle"
                          checked={iconStyle === ICON_STYLE_REGULAR}
                          onChange={() => setIconStyle(ICON_STYLE_REGULAR)}
                          className="h-4 w-4 border-neutral-300 text-indigo-500 focus:ring-indigo-500"
                        />
                        <span className="text-sm text-neutral-700">Regular</span>
                      </div>
                      <span className="text-xs text-indigo-500">
                        {iconData?.namesByStyle[ICON_STYLE_REGULAR]?.length ?? 0}
                      </span>
                    </label>
                    <label className="flex cursor-pointer items-center justify-between rounded-md px-2 py-1.5 hover:bg-neutral-50">
                      <div className="flex items-center gap-2">
                        <input
                          type="radio"
                          name="iconStyle"
                          checked={iconStyle === ICON_STYLE_SOLID}
                          onChange={() => setIconStyle(ICON_STYLE_SOLID)}
                          className="h-4 w-4 border-neutral-300 text-indigo-500 focus:ring-indigo-500"
                        />
                        <span className="text-sm text-neutral-700">Solid</span>
                      </div>
                      <span className="text-xs text-indigo-500">
                        {iconData?.namesByStyle[ICON_STYLE_SOLID]?.length ?? 0}
                      </span>
                    </label>
                  </div>

                  <button
                    type="button"
                    onClick={() => setShowStyleFilter(false)}
                    className="mt-3 w-full rounded-md bg-indigo-500 py-2 text-sm font-medium text-white hover:bg-indigo-600"
                  >
                    Done
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-3">
          {!hasResults ? (
            <div className="flex h-full items-center justify-center text-sm text-neutral-500">
              {isSearching ? 'No icons found' : <Loader2 className="h-5 w-5 animate-spin" />}
            </div>
          ) : isSearching ? (
            <div>
              <h3 className="mb-2 text-xs font-semibold text-neutral-500">
                Search Results ({searchResults?.length})
              </h3>
              <div className="grid grid-cols-4 gap-1">
                {searchResults?.map((iconName) => (
                  <IconButton
                    key={iconName}
                    iconName={iconName}
                    formattedName={formatIconName(iconName)}
                    iconStyle={iconStyle}
                    isSelected={selectedIcon === iconName}
                    onSelect={handleSelect}
                  />
                ))}
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredCategories &&
                Object.entries(filteredCategories).map(([category, icons]) => (
                  <div key={category}>
                    <h3 className="mb-2 text-xs font-semibold text-neutral-500">{category}</h3>
                    <div className="grid grid-cols-4 gap-1">
                      {icons.map((iconName) => (
                        <IconButton
                          key={iconName}
                          iconName={iconName}
                          formattedName={formatIconName(iconName)}
                          iconStyle={iconStyle}
                          isSelected={selectedIcon === iconName}
                          onSelect={handleSelect}
                        />
                      ))}
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
